import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import { z } from "zod";
import {
  MiDetailFields,
  MiModalConfirm,
  MiProfilePicture,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  INPUT_TYPES,
  MODAL_TITLES,
  VENDOR_TYPE_OPTIONS,
  regionCodesUS,
} from "@miplatform/constants";
import { AccountType } from "@miplatform/types";
import { getRootUrl } from "@miplatform/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { Footer } from "@mibuildweb/components";
import { usePermissions } from "@mibuildweb/hooks";
import { Box } from "@mui/material";
import {
  ACCOUNTS_TAB_TO_PERMISSIONS,
  isVendorAccountType,
} from "../../Accounts/accountsConstants";
import { ConfigAccordionHeader } from "../../Config/components/ConfigAccordionHeader/ConfigAccordionHeader";
import { useAccountSummaryTabViewModel } from "./useAccountSummaryTabViewModel";

const schema = z
  .object({
    name: z
      .string()
      .min(1, "Builder name is required")
      .regex(/^[^:"]*$/, "Name cannot contain colons or double quotes"),
    code: z.string().min(1, "Abbreviation is required"),
    paymentTerms: z
      .string()
      .max(50, "Payment terms must be less than 50 characters")
      .optional(),
    warrantyPeriodInMonths: z
      .number()
      .min(0, "Warranty period must be non-negative")
      .nullable(),
    enableRetainage: z.boolean().optional(),
    defaultRetainagePercentage: z.preprocess(
      (val) => (val === "" || val === null || val === undefined ? null : val),
      z.coerce
        .number()
        .min(0.01, "Retainage percentage must be greater than 0%")
        .max(100, "Retainage percentage cannot exceed 100%")
        .nullable()
    ),
    enableDiscount: z.boolean().optional(),
    defaultDiscountPercentage: z.preprocess(
      (val) => (val === "" || val === null || val === undefined ? null : val),
      z.coerce
        .number()
        .min(0.01, "Discount percentage must be greater than 0%")
        .max(100, "Discount percentage cannot exceed 100%")
        .nullable()
    ),
    supplierType: z.string().optional(),
    address: z.object({
      addressLine1: z
        .string()
        .min(1, "Address is required")
        .regex(
          /^[^:"]*$/,
          "Address line 1 cannot contain colons or double quotes"
        ),
      addressLine2: z
        .string()
        .regex(
          /^[^:"]*$/,
          "Address line 2 cannot contain colons or double quotes"
        )
        .optional(),
      city: z.string().min(1, "City is required"),
      regionCode: z.string().min(1, "State is required"),
      countryCode: z.string().optional(),
      postalCode: z
        .string()
        .min(5, "Zip is required")
        .max(5, "Zip must be 5 digits"),
    }),
    billingAddress: z.object({
      addressLine1: z
        .string()
        .min(1, "Address is required")
        .regex(
          /^[^:"]*$/,
          "Address line 1 cannot contain colons or double quotes"
        ),
      addressLine2: z
        .string()
        .regex(
          /^[^:"]*$/,
          "Address line 2 cannot contain colons or double quotes"
        )
        .optional(),
      city: z.string().min(1, "City is required"),
      regionCode: z.string().min(1, "State is required"),
      countryCode: z.string().optional(),
      postalCode: z
        .string()
        .min(5, "Zip is required")
        .max(5, "Zip must be 5 digits"),
    }),
  })
  .superRefine((data, ctx) => {
    if (
      data.enableRetainage &&
      (data.defaultRetainagePercentage === null ||
        data.defaultRetainagePercentage === undefined)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Retainage percentage is required when retainage is enabled",
        path: ["defaultRetainagePercentage"],
      });
    }

    if (
      data.enableDiscount &&
      (data.defaultDiscountPercentage === null ||
        data.defaultDiscountPercentage === undefined)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Discount percentage is required when discount is enabled",
        path: ["defaultDiscountPercentage"],
      });
    }
  });

export function AccountSummaryTab({
  tab,
  stateManager,
  setAccountDetail,
  accountDetail,
  getAccountDetails,
}) {
  const [openConfirmModal, setOpenConfirmModal] = useState(false);

  const {
    formState: { isValid, isDirty },
    control,
    reset,
    setValue,
    getValues,
    handleSubmit,
    watch,
  } = useForm({
    mode: "onChange",
    defaultValues: {
      name: "",
      code: "",
      paymentTerms: "",
      warrantyPeriodInMonths: null,
      enableRetainage: false,
      defaultRetainagePercentage: null,
      enableDiscount: false,
      defaultDiscountPercentage: null,
      supplierType: "",
      address: {
        addressLine1: "",
        addressLine2: "",
        city: "",
        regionCode: "",
        countryCode: "",
        postalCode: "",
      },
      billingAddress: {
        addressLine1: "",
        addressLine2: "",
        city: "",
        regionCode: "",
        countryCode: "",
        postalCode: "",
      },
    },
    resolver: zodResolver(schema),
  });

  const { hasPermission } = usePermissions();

  const { classes } = useStyles();
  const [pendingProfilePicture, setPendingProfilePicture] = useState(null);

  const enableRetainage = watch("enableRetainage");
  const enableDiscount = watch("enableDiscount");

  const editMode = hasPermission(ACCOUNTS_TAB_TO_PERMISSIONS[tab]?.UPDATE);

  const {
    isLoading,
    profilePicture,
    handleProfilePictureChange,
    handleRemoveProfilePicture,
    handleUpdateAccount,
  } = useAccountSummaryTabViewModel({
    stateManager,
    accountDetail,
    setAccountDetail,
    getAccountDetails,
    setOpenConfirmModal,
  });

  const getFormDataFromAccountDetail = (detail) => ({
    name: detail.name ?? "",
    code: detail.code ?? "",
    paymentTerms: detail.paymentTerms ?? "",
    warrantyPeriodInMonths: detail.warrantyPeriodInMonths ?? null,
    enableRetainage: detail.defaultRetainagePercentage != null,
    defaultRetainagePercentage: detail.defaultRetainagePercentage ?? null,
    enableDiscount: detail.defaultDiscountPercentage != null,
    defaultDiscountPercentage: detail.defaultDiscountPercentage ?? null,
    supplierType: detail.supplierType ?? "",
    address: {
      addressLine1: detail.address?.addressLine1 ?? "",
      addressLine2: detail.address?.addressLine2 ?? "",
      city: detail.address?.city ?? "",
      regionCode: detail.address?.regionCode ?? "",
      countryCode: detail.address?.countryCode ?? "",
      postalCode: detail.address?.postalCode ?? "",
    },
    billingAddress: {
      addressLine1: detail.billingAddress?.addressLine1 ?? "",
      addressLine2: detail.billingAddress?.addressLine2 ?? "",
      city: detail.billingAddress?.city ?? "",
      regionCode: detail.billingAddress?.regionCode ?? "",
      countryCode: detail.billingAddress?.countryCode ?? "",
      postalCode: detail.billingAddress?.postalCode ?? "",
    },
  });

  useEffect(() => {
    if (accountDetail.id) {
      reset(getFormDataFromAccountDetail(accountDetail));
      setPendingProfilePicture(null);
    }
  }, [accountDetail, reset]);

  const basicInfoFieldset = [
    {
      formControl: control,
      label: "Customer Name",
      name: "name",
      width: "50%",
      required: true,
      readOnly: !editMode,
      disabled: !editMode,
    },
    {
      formControl: control,
      label: "Abbreviation",
      name: "code",
      width: "50%",
      required: true,
      readOnly: !editMode,
      disabled: !editMode,
      inputProps: { maxLength: 6 },
    },
    {
      formControl: control,
      name: "paymentTerms",
      label: "Invoice Payment Terms",
      width: "50%",
      multiline: true,
      readOnly: !editMode,
      disabled: !editMode,
    },

    {
      formControl: control,
      fieldType: INPUT_TYPES.NUMERIC,
      readOnly: !editMode,
      disabled: !editMode,
      allowNegative: false,
      name: "warrantyPeriodInMonths",
      label: "Warranty Period (years)",
      width: "50%",
      style: {
        display:
          accountDetail.accountType === AccountType.Builder ? "block" : "none",
      },
      value: getValues("warrantyPeriodInMonths")
        ? getValues("warrantyPeriodInMonths") / 12
        : null,
      setValue: (value) => {
        setValue("warrantyPeriodInMonths", value ? value * 12 : null, {
          shouldValidate: true,
          shouldDirty: true,
        });
      },
    },
  ];

  const financialSettingsFieldset = [
    {
      formControl: control,
      name: "enableRetainage",
      label: "Enable Default Retainage",
      width: "100%",
      fieldType: INPUT_TYPES.CHECKBOX,
      readOnly: !editMode,
      disabled: !editMode,
      setValue: (event) => {
        setValue("enableRetainage", event.target.checked, {
          shouldValidate: true,
          shouldDirty: true,
        });
        if (!event.target.checked) {
          setValue("defaultRetainagePercentage", null, {
            shouldValidate: true,
            shouldDirty: true,
          });
        }
      },
    },
    ...(enableRetainage
      ? [
          {
            formControl: control,
            name: "defaultRetainagePercentage",
            label: "Retainage Percentage",
            helperText: "Required - Enter percentage to retain from payments",
            width: "40%",
            required: true,
            fixedDecimalScale: 2,
            allowNegative: false,
            fieldType: INPUT_TYPES.NUMERIC,
            suffix: "%",
            placeholder: "Enter percentage (e.g., 10.00)",
            value: getValues("defaultRetainagePercentage"),
            setValue: (value) => {
              setValue("defaultRetainagePercentage", value, {
                shouldValidate: true,
                shouldDirty: true,
              });
            },
            readOnly: !editMode,
            disabled: !editMode,
            style: {
              marginLeft: "1.5rem",
            },
          },
        ]
      : []),

    {
      formControl: control,
      name: "enableDiscount",
      label: "Enable Default Discount",
      helperText: "Set a default discount percentage for all projects",
      width: "100%",
      fieldType: INPUT_TYPES.CHECKBOX,
      readOnly: !editMode,
      disabled: !editMode,
      setValue: (event) => {
        setValue("enableDiscount", event.target.checked, {
          shouldValidate: true,
          shouldDirty: true,
        });
        if (!event.target.checked) {
          setValue("defaultDiscountPercentage", null, {
            shouldValidate: true,
            shouldDirty: true,
          });
        }
      },
    },
    ...(enableDiscount
      ? [
          {
            formControl: control,
            name: "defaultDiscountPercentage",
            label: "Discount Percentage",
            helperText: "Required - Enter percentage to discount from total",
            width: "40%",
            required: true,
            fixedDecimalScale: 2,
            allowNegative: false,
            fieldType: INPUT_TYPES.NUMERIC,
            suffix: "%",
            placeholder: "Enter percentage (e.g., 5.00)",
            value: getValues("defaultDiscountPercentage"),
            setValue: (value) => {
              setValue("defaultDiscountPercentage", value, {
                shouldValidate: true,
                shouldDirty: true,
              });
            },
            readOnly: !editMode,
            disabled: !editMode,
            style: {
              marginLeft: "1.5rem",
            },
          },
        ]
      : []),
    {
      formControl: control,
      label: "Supplier Type",
      name: "supplierType",
      width: "50%",
      fieldType: INPUT_TYPES.SELECT,
      options: VENDOR_TYPE_OPTIONS,
      required: isVendorAccountType(accountDetail.accountType),
      defaultValue: "Other",
      style: {
        display: isVendorAccountType(accountDetail.accountType)
          ? "block"
          : "none",
      },
      readOnly: !editMode,
      disabled: !editMode,
    },
  ];
  const addressInfoFieldset = [
    {
      formControl: control,
      label: "Address Line 1",
      name: "address.addressLine1",
      width: "50%",
      required: true,
      readOnly: !editMode,
      disabled: !editMode,
    },
    {
      formControl: control,
      label: "Address Line 2",
      name: "address.addressLine2",
      width: "50%",
      readOnly: !editMode,
      disabled: !editMode,
    },
    {
      formControl: control,
      label: "City",
      name: "address.city",
      width: "50%",
      required: true,
      readOnly: !editMode,
      disabled: !editMode,
    },
    {
      formControl: control,
      label: "State",
      name: "address.regionCode",
      fieldType: INPUT_TYPES.SELECT,
      options: regionCodesUS.map((code) => ({ label: code, value: code })),
      width: "50%",
      required: true,
      readOnly: !editMode,
      disabled: !editMode,
    },
    {
      formControl: control,
      label: "Country",
      name: "address.countryCode",
      width: "50%",
      readOnly: !editMode,
      disabled: !editMode,
      inputProps: { maxLength: 3 },
    },
    {
      formControl: control,
      label: "Zip",
      name: "address.postalCode",
      width: "50%",
      required: true,
      readOnly: !editMode,
      disabled: !editMode,
      type: "number",
    },
  ];
  const billingAddressInfoFieldset = [
    {
      formControl: control,
      label: "Address Line 1",
      name: "billingAddress.addressLine1",
      width: "50%",
      required: true,
      readOnly: !editMode,
      disabled: !editMode,
    },
    {
      formControl: control,
      label: "Address Line 2",
      name: "billingAddress.addressLine2",
      width: "50%",
      readOnly: !editMode,
      disabled: !editMode,
    },
    {
      formControl: control,
      label: "City",
      name: "billingAddress.city",
      width: "50%",
      required: true,
      readOnly: !editMode,
      disabled: !editMode,
    },
    {
      formControl: control,
      label: "State",
      name: "billingAddress.regionCode",
      fieldType: INPUT_TYPES.SELECT,
      options: regionCodesUS.map((code) => ({ label: code, value: code })),
      width: "50%",
      required: true,
      readOnly: !editMode,
      disabled: !editMode,
    },
    {
      formControl: control,
      label: "Country",
      name: "billingAddress.countryCode",
      width: "50%",
      readOnly: !editMode,
      disabled: !editMode,
      inputProps: { maxLength: 3 },
    },
    {
      formControl: control,
      label: "Zip",
      name: "billingAddress.postalCode",
      width: "50%",
      required: true,
      readOnly: !editMode,
      disabled: !editMode,
      type: "number",
    },
  ];

  const getProfilePictureSrc = () => {
    if (pendingProfilePicture) {
      return URL.createObjectURL(pendingProfilePicture);
    }
    if (profilePicture) {
      return `${getRootUrl()}/${profilePicture.referenceId}`;
    }
    return undefined;
  };

  return (
    <Box display="flex" flexDirection="column" height="100%" overflow="hidden">
      <Box padding={4} flex={1} overflow="auto">
        <Box className={classes.detailsContainer}>
          <ConfigAccordionHeader title="Account Summary">
            <Box display="flex" alignItems="center" mb={2} gap={8}>
              <MiProfilePicture
                src={getProfilePictureSrc()}
                code={accountDetail.code}
                size={90}
                disabled={isLoading || !accountDetail || !editMode}
                onEdit={
                  editMode
                    ? (file) => setPendingProfilePicture(file)
                    : undefined
                }
                onDelete={
                  editMode
                    ? () => {
                        if (pendingProfilePicture) {
                          setPendingProfilePicture(null);
                        } else {
                          handleRemoveProfilePicture();
                        }
                      }
                    : undefined
                }
              />
              <MiDetailFields detailFields={basicInfoFieldset} />
            </Box>
          </ConfigAccordionHeader>

          <ConfigAccordionHeader title="Financial Settings">
            <MiDetailFields detailFields={financialSettingsFieldset} />
          </ConfigAccordionHeader>

          <ConfigAccordionHeader title="Address Information">
            <MiDetailFields detailFields={addressInfoFieldset} />
          </ConfigAccordionHeader>

          <ConfigAccordionHeader title="Billing Address Information">
            <MiDetailFields detailFields={billingAddressInfoFieldset} />
          </ConfigAccordionHeader>
        </Box>

        <MiModalConfirm
          title={MODAL_TITLES.SAVE}
          disableClick={isLoading}
          textPositive="Yes, Update"
          open={openConfirmModal}
          handleNegative={() => setOpenConfirmModal(false)}
          handlePositive={async () => {
            if (pendingProfilePicture) {
              await handleProfilePictureChange(pendingProfilePicture);
              setPendingProfilePicture(null);
            }
            handleSubmit((values) => handleUpdateAccount(values))();
          }}
          description={`You are about to save changes to ${accountDetail.name}. Are you sure you would like to continue?`}
          buttonPositiveColor={BUTTON_COLORS.PRIMARY}
        />
      </Box>
      <Footer
        show={editMode}
        saveTitle="Save Summary"
        isDirty={isDirty || pendingProfilePicture !== null}
        handleSaveClick={() => setOpenConfirmModal(true)}
        handleCancelClick={() => {
          reset(getFormDataFromAccountDetail(accountDetail));
          setPendingProfilePicture(null);
        }}
        disableSave={
          !isValid || isLoading || (pendingProfilePicture === null && !isDirty)
        }
      />
    </Box>
  );
}

const useStyles = makeStyles()((theme) => ({
  container: {
    display: "flex",
    height: "100%",
    gap: theme.spacing(2),
    overflow: "hidden",

    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      overflow: "auto",
      paddingBottom: theme.spacing(8),
    },
  },
  userContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    flex: 0.4,
    padding: "1rem",
    justifyContent: "space-between ",

    [theme.breakpoints.down("sm")]: {
      flex: "none",
      width: "100%",
    },
  },
  userTopContainer: {
    marginTop: 20,
    width: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
  userRoleContainer: {
    marginTop: 30,
    width: "100%",
    minWidth: "100px",
    textAlign: "center",
  },
  userName: {
    fontSize: theme.fontSize.large,
    fontWeight: theme.fontWeight.bold,
  },
  userRole: {
    color: theme.palette.grey.main,
  },
  detailsContainer: {
    display: "flex",
    gap: theme.spacing(2),
    flexDirection: "column",
    height: "100%",
    overflowY: "auto",

    [theme.breakpoints.down("sm")]: {
      flex: "none",
      width: "100%",
      maxWidth: "100%",

      height: "auto",
    },
  },
}));
